import { createRoute, defer, redirect } from '@tanstack/react-router'
import { z } from 'zod'

import { OverviewErrorComponent, ShipmentErrorComponent } from '@/features/shipments/view/shipmentLayout'
import { entitySubscriptionsQueryOptions } from '@/features/subscriptions'
import { dangerousGoodNumbersQueryOptions } from '@/features/transportUnits'
import { rootRoute } from '@/routes/root'
import { ensureAuthenticatedUser } from '@/utils/auth-wall'
import { Features } from '@/utils/featureFlag/features'
import { waitForShipmentToExist } from '@/utils/shipments'

import { houseShipmentByCodeQueryOptions } from './api/houseShipments.service'
import { priceBreakdownsQueryOptions } from './api/priceBreakdownsQueryOptions'
import { shipmentQueryOptions } from './api/useShipment'
import { shipmentEmissionsQueryOptions } from './api/useShipmentEmissions'
import { OverviewSearchType } from './types'
import { decodeFilter, decodeSort, getDefaultSort } from './utils/overviewFilters'
import { ShipmentContainersSkeleton } from './view/containers/containersSkeleton'
import { DemDetSkeleton } from './view/demDet/demDetSkeleton'
import { CustomsSkeleton } from './view/details/customsSkeleton'
import { ShipmentDetailsSkeleton } from './view/details/detailsSkeleton'
import { ShipmentDocumentsSkeleton } from './view/details/documentsSkeleton'
import { ShipmentPriceBreakdownSkeleton } from './view/priceBreakdown/priceBreakdownSkeleton'
import { ShipmentSkeleton } from './view/shipment/shipmentSkeletons'
import { OverviewSkeleton } from './view/shipmentsSkeleton'

export const overviewSearchSchema = z.object({
  sort: z
    .object({ field: z.string(), direction: z.enum(['asc', 'desc']) })
    .nullish()
    .transform((data) => data ?? undefined),
  filter: z
    .object({})
    .passthrough()
    .nullish()
    .transform((data) => data ?? undefined),
  showBookingsOnly: z
    .boolean()
    .nullish()
    .transform((data) => data ?? undefined),
  showShipmentsOnly: z
    .boolean()
    .nullish()
    .transform((data) => data ?? undefined),
})

export const overviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: (search: OverviewSearchType) => {
    try {
      overviewSearchSchema.parse({
        filter: decodeFilter(search.filter),
        sort: decodeSort(search.sort, getDefaultSort()),
        showBookingsOnly: search.showBookingsOnly,
        showShipmentsOnly: search.showShipmentsOnly,
      })
      return search
    } catch (error) {
      if (error instanceof z.ZodError) {
        // eslint-disable-next-line no-console
        console.error('Zod validation error:', error.errors)
      }
      throw error
    }
  },
  beforeLoad: ensureAuthenticatedUser,
  errorComponent: OverviewErrorComponent,
  pendingComponent: OverviewSkeleton,
}).lazy(() => import('./view/shipments').then((d) => d.Route))

export const shipmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shipments',
  beforeLoad: ensureAuthenticatedUser,
  loader: () => {
    throw redirect({ to: '/' })
  },
})

export const shipmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shipments/$id',
  validateSearch: z.object({
    openCancelDialog: z
      .boolean()
      .nullish()
      .transform((data) => data ?? undefined),
    // Set when navigating here straight after placing a booking. The shipment
    // may not be queryable yet (read-after-write propagation), so the loader
    // waits for it to exist instead of immediately failing into the error page.
    isNewlyCreated: z
      .boolean()
      .nullish()
      .transform((data) => data ?? undefined),
  }).parse,
  loaderDeps: ({ search: { isNewlyCreated } }) => ({ isNewlyCreated }),
  errorComponent: ShipmentErrorComponent,
  beforeLoad: async (args) => {
    await ensureAuthenticatedUser(args)
    const id = args.params.id
    const isNewlyCreated = args.search.isNewlyCreated
    // For a just-created shipment, wait (silently) for it to become queryable
    // before loading. This shows the pending skeleton during the propagation
    // window rather than rendering ShipmentErrorComponent on a transient 404.
    if (isNewlyCreated) {
      await waitForShipmentToExist(id, 50)
    }
  },
  loader: async ({ context, params: { id } }) => {
    if (!context.queryClient) return

    const emissions = context.queryClient?.ensureQueryData(shipmentEmissionsQueryOptions(id))
    const subscriptions = context.queryClient?.ensureQueryData(entitySubscriptionsQueryOptions(id))
    const shipment = context?.queryClient?.ensureQueryData(houseShipmentByCodeQueryOptions(id))

    return { shipment, emissions: defer(emissions), subscriptions: defer(subscriptions) }
  },
  pendingComponent: ShipmentSkeleton,
}).lazy(() => import('./view/shipment/shipment').then((d) => d.Route))

export const shipmentDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shipments/$id/details',
  beforeLoad: ensureAuthenticatedUser,
  errorComponent: ShipmentErrorComponent,
  loader: async ({ context, params: { id } }) => {
    if (!context.queryClient) return
    const shipment = context.queryClient?.ensureQueryData(shipmentQueryOptions(id))
    return { shipment: defer(shipment) }
  },
  pendingComponent: ShipmentDetailsSkeleton,
}).lazy(() => import('./view/details/details').then((d) => d.Route))

export const shipmentContainersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shipments/$id/containers',
  beforeLoad: ensureAuthenticatedUser,
  errorComponent: ShipmentErrorComponent,
  loader: async ({ context, params: { id } }) => {
    if (!context.queryClient) return
    const shipment = context.queryClient?.ensureQueryData(shipmentQueryOptions(id))
    const unNumbersUnits = context.queryClient?.ensureQueryData(dangerousGoodNumbersQueryOptions)
    return { shipment: defer(shipment), unNumbersUnits: defer(unNumbersUnits) }
  },
  pendingComponent: ShipmentContainersSkeleton,
}).lazy(() => import('./view/containers/containers').then((d) => d.Route))

export const shipmentPriceBreakdownRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shipments/$id/price-breakdown',
  beforeLoad: ensureAuthenticatedUser,
  errorComponent: ShipmentErrorComponent,
  loader: async ({ context, params: { id } }) => {
    if (!context.queryClient) return
    const priceBreakdowns = context.queryClient?.ensureQueryData(priceBreakdownsQueryOptions(id))
    return { priceBreakdowns: defer(priceBreakdowns) }
  },
  pendingComponent: ShipmentPriceBreakdownSkeleton,
}).lazy(() => import('./view/priceBreakdown/priceBreakdownRoot').then((d) => d.Route))

export const shipmentChargesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shipments/$id/dem-det',
  beforeLoad: async ({ context, location }) => {
    await ensureAuthenticatedUser({ context, location })
    if (!context.isFeatureEnabled!(Features.DEM_DET)) {
      throw redirect({ to: '/' })
    }
  },
  errorComponent: ShipmentErrorComponent,
  loader: async ({ context, params: { id } }) => {
    if (!context.queryClient) return
    const houseShipment = context.queryClient?.ensureQueryData(houseShipmentByCodeQueryOptions(id))
    return { houseShipment: defer(houseShipment) }
  },
  pendingComponent: DemDetSkeleton,
}).lazy(() => import('./view/demDet/demDetRoot').then((d) => d.Route))

export const shipmentCustomsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shipments/$id/customs',
  beforeLoad: ensureAuthenticatedUser,
  errorComponent: ShipmentErrorComponent,
  pendingComponent: CustomsSkeleton,
}).lazy(() => import('./view/details/customs').then((d) => d.Route))

export const shipmentDocumentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shipments/$id/documents',
  beforeLoad: ensureAuthenticatedUser,
  errorComponent: ShipmentErrorComponent,
  loader: async ({ context, params: { id } }) => {
    if (!context.queryClient) return
    const shipment = context.queryClient?.ensureQueryData(houseShipmentByCodeQueryOptions(id))
    return { shipment: defer(shipment) }
  },
  pendingComponent: ShipmentDocumentsSkeleton,
}).lazy(() => import('./view/details/documents').then((d) => d.Route))
